import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { catchError, delay, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { RootFacade } from '../abstraction/root.facade';
import { HttpBaseResponse } from '../shared/models/httpBaseResponse';
import { PortalLoginData, Tenant, TenantData } from '../shared/models/tenant';
import { User } from '../shared/models/user';
import { APIS } from './enums/apis';
import { StorageService } from './services/storage.service';
import { USERADMIN } from './enums/userAdmin';
import { USERFRONTDESK } from './enums/userFrontDesk';
import { ExperienceI18nService } from './experience/experience-i18n.service';
import { ExperienceThemeService } from './experience/experience-theme.service';
import { UserTenantAccess } from '../shared/models/submodels/userTenantAccess';
import { TenantConfigService } from '../tenant-config/tenant-config.service';
import Swal from 'sweetalert2';

export interface PostUserBody {
    id: string;
}
export interface PatchUserBody {
    active: boolean;
}
interface PostBodyPasswordReset {
    email: string;
    applicationId: string;
    // tenantId: number;
}
@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private user$: BehaviorSubject<User>;
    private tenant$: BehaviorSubject<Tenant>;
    private _redirectUrl: string;
    private applicationId = '2';

    getAuthApplicationId(): string {
        return this.applicationId;
    }

    constructor(
        private rootFacade: RootFacade,
        public dialogRef: MatDialog,
        private http: HttpClient,
        private storageService: StorageService,
        private experienceTheme: ExperienceThemeService,
        private experienceI18n: ExperienceI18nService,
        private tenantConfig: TenantConfigService,
    ) {
        this.user$ = new BehaviorSubject<User>(AuthenticationService.readUserFromLocalStorage());
        this.tenant$ = new BehaviorSubject<Tenant>(AuthenticationService.readTenantFromLocalStorage());
    }

    private static readTenantFromLocalStorage(): Tenant | null {
        const raw = localStorage.getItem('tenant');
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw) as Tenant;
        } catch {
            return null;
        }
    }

    // A profile without id is not a usable session ({}, corrupted or partially written)
    private static isMeaningfulProfile(u: User | null | undefined): u is User {
        return !!u && typeof u === 'object' && u.id != null;
    }

    private static readUserFromLocalStorage(): User | null {
        const raw = localStorage.getItem('user');
        if (!raw) {
            return null;
        }
        try {
            const u = JSON.parse(raw) as User;
            if (!AuthenticationService.isMeaningfulProfile(u)) {
                // If empty user drop it instead of treating it as a valid session
                localStorage.removeItem('user');
                return null;
            }
            const t = u?.token;
            if (t && (t.accessToken || t.refreshToken)) {
                const { token: _drop, ...profile } = u;
                localStorage.setItem('user', JSON.stringify(profile));
                return { ...profile, token: t };
            }
            return u;
        } catch {
            localStorage.removeItem('user');
            return null;
        }
    }

    private persistUserProfileOnly(u: User | null): void {
        if (u == null) {
            localStorage.removeItem('user');
            return;
        }
        const { token: _t, ...profile } = u;
        localStorage.setItem('user', JSON.stringify(profile));
    }

    bootstrapSessionFromCookies(): Observable<void> {
        if (this.user$.value?.token?.accessToken) {
            return of(undefined);
        }
        const headers = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post(`${environment.apiAuthService}${APIS.REFRESH}`, { applicationId: this.getAuthApplicationId() }, {
                headers,
                withCredentials: true,
                responseType: 'text',
            })
            .pipe(
                tap((access) => {
                    this.updateCurrentUser({ token: { accessToken: access, refreshToken: null } });
                }),
                switchMap(() => this.hydrateUserProfileIfNeeded()),
                map(() => void 0),
                catchError(() => of(void 0)),
            );
    }

    /**
     * After a cookie-based refresh, the in-memory user may still be an empty/incomplete profile
     * ({}, or first load with no local cache at all). Without this, updateCurrentUser
     * would keep/persist that empty object instead of the real profile (id, roles, etc.), leaving
     * isAdmin()/isFrontDesk() and personalization broken even though the session is valid
     */
    private hydrateUserProfileIfNeeded(): Observable<unknown> {
        if (AuthenticationService.isMeaningfulProfile(this.user$.value)) {
            return of(undefined);
        }
        return this.getUserData().pipe(
            tap((user) => this.updateCurrentUser(user)),
            catchError(() => of(undefined)),
        );
    }

    get redirectUrl(): string {
        return this._redirectUrl;
    }

    set redirectUrl(url: string) {
        this._redirectUrl = url;
    }

    // Get user value without subscription
    get userValue(): User {
        return this.user$.value;
    }

    get tenantValue(): Tenant {
        return this.tenant$.value;
    }

    // Get user value by subscription
    getUser$(): Observable<User> {
        return this.user$.asObservable();
    }

    // Get user value by subscription
    getTenant$(): Observable<Tenant> {
        return this.tenant$.asObservable();
    }

    isAuthenticated() {
        return !!this.userValue?.token?.accessToken;
    }

    isAuthenticatedCookie(): Observable<boolean> {
        return this.bootstrapSessionFromCookies().pipe(
            map(() => this.isAuthenticated()),
            catchError(() => of(false)),
        );
    }

    updateCurrentUser(user: User) {
        if (!!this.userValue) {
            const u = { ...this.userValue, ...user };
            const prevTok = this.userValue.token;
            const nextTok = user.token;
            if (prevTok != null && nextTok != null) {
                u.token = {
                    accessToken: nextTok.accessToken ?? prevTok.accessToken,
                    refreshToken: nextTok.refreshToken ?? prevTok.refreshToken,
                };
            }
            this.user$.next(u);
            this.persistUserProfileOnly(u);
        } else {
            this.user$.next(user);
            this.persistUserProfileOnly(user);
        }
    }

    async updateCurrentTenant(
        tenant: any,
        options: { reloadExperience?: boolean } = {},
    ): Promise<void> {
        if (!!this.tenantValue) {
            // user exists, we must update its value
            const t = { ...this.tenantValue, ...tenant };
            // Save the updated object in local storage
            localStorage.setItem('tenant', JSON.stringify(t));
            // Update the BehaviourSubject with the new user
            this.tenant$.next(t);
        } else {
            // There's no user yet
            localStorage.setItem('tenant', JSON.stringify(tenant));
            // Update the BehaviourSubject with the new user
            this.tenant$.next(tenant);
        }
        if (options.reloadExperience !== false) {
            await Promise.all([
                this.experienceTheme.reload(),
                this.experienceI18n.reload(),
            ]);
        }
    }

    removeCurrentUser() {
        // Remove user from local storage to log out
        localStorage.removeItem('user');
        // Set the currentUserSubject to null, indicating that the user is no longer logged in the app
        this.user$.next(null);
        this._redirectUrl = null;
    }

    removeCurrentTenant() {
        // Remove user from local storage to log out
        localStorage.removeItem('tenant');
        // Set the currentUserSubject to null, indicating that the user is no longer logged in the app
        this.tenant$.next(null);
        //void this.experienceTheme.reload();
        //void this.experienceI18n.reload();
    }

    //  login(username: string, password: string): Observable<any> {
    //     const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
    //     const body = {
    //        username: username,
    //        password: password,
    //        applicationId: this.applicationId,
    //        tenantCode: this.tenantValue?.id // TODO: descomentar cuando se vuelve a DEV
    //        //tenantCode: 8 // TODO: descomentar cuando se desea ejecutar QA
    //     };

    //     return this.http
    //        .post<any>(`${environment.apiAuthService}${APIS.LOGIN}`, body, {
    //           headers: queryHeaders,
    //           observe: 'response',
    //        })
    //        .pipe(
    //           map<HttpResponse<any>, any>((response) => {
    //              //  this.router.navigate([ROUTES.LOGIN]);
    //              return response.body.data;
    //           }),
    //        );
    //  }

    loginV2(username: string, password: string, tenantId?: number): Observable<HttpBaseResponse<TenantData>> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const body = {
            username: username,
            password: password,
            applicationId: this.applicationId,
            tenantId
        };

        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.LOGIN_V2}`, body, {
                headers: queryHeaders,
                observe: 'response',
                withCredentials: true,
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body;
                }),
            );
    }

    //TODO: Resolve tenantId for reset password (RESOLVED)
    postResetPassword(email: string): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const body: PostBodyPasswordReset = {
            email,
            applicationId: this.applicationId,
            // tenantId: 1
        };
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.USERS}/password-reset`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postUserFinancier(body: PostUserBody): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.USER}${APIS.USERS}/financiers`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postUserOperator(body: PostUserBody): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.USER}${APIS.USERS}/operators`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postUserProvider(body: PostUserBody): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.USER}${APIS.USERS}/providers`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    postUserPatient(body: PostUserBody): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.USER}${APIS.USERS}/patients`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    patchUser(body: PatchUserBody, userId: string): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .patch<any>(`${environment.apiAuthService}${APIS.USER}${APIS.USERS}/${userId}`, body, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    logout(): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        // Cleanup local session state regardless of the backend outcome (e.g. refresh token
        // already expired/invalid in FusionAuth can make POST /logout respond with an error).
        const cleanupLocalSession = () => {
            this.removeCurrentTenant();
            this.removeCurrentUser();
            this.tenantConfig.reset();
            this.rootFacade.cleanStore();
        };
        Swal.close();
        this.dialogRef.closeAll();
        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.LOGOUT}`, {}, {
                headers: queryHeaders,
                observe: 'response',
                withCredentials: true,
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body;
                }),
                tap(() => cleanupLocalSession()),
                catchError((err) => {
                    cleanupLocalSession();
                    return of(null); // Doesn't return the error
                }),
                finalize(() => {
                    this.storageService.removeValuesByKeyOnLocalStorage("consolidationsFilters");
                    this.clearSessionStorage();
                })
            );
    }

    clearSessionStorage() {
        sessionStorage.clear();
    }

    getUserData(): Observable<User> {
        const queryHeaders = new HttpHeaders().set('Content-Type', 'application/json');

        return this.http
            .get<HttpBaseResponse<any>>(`${environment.apiAuthService}${APIS.USER}`, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<HttpBaseResponse<any>>, User>((response) => {
                    return response.body.data;
                }),
            );
    }

    isReachable(): Observable<any> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');

        return this.http
            .get<HttpBaseResponse<any>>(`https://api.adomicilio-sa.com.ar/`, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<HttpBaseResponse<any>>, User>((response) => {
                    return response.body.data;
                }),
            );
    }

    postUser(entityId) {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const body = {
            entityId
        };
        return this.http
            .post<HttpBaseResponse<any>>(`${environment.apiAuthService}${APIS.USERS}`,
                JSON.stringify(body),
                {
                    headers: queryHeaders,
                    observe: 'response',
                })
            .pipe(
                map<HttpResponse<any>, Tenant>((response) => {
                    return response.body.data;
                }),
            );
    }

    verifyPassword(password: string): Observable<void> {
        return this.http.post<void>(
            `${environment.apiAuthService}${APIS.VERIFY_PASSWORD}`,
            { password },
            { withCredentials: true },
        ).pipe(map(() => void 0));
    }

    postAttention(attentions: { fromDate: Date, toDate: Date, stateId: number, agreementId: number }[]) {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const body = attentions.map(attention => {
            const { stateId, ...noStateId } = attention;
            return { ...noStateId, state: { id: stateId } };
        });
        return this.http
            .post<any>(
                `${environment.apiAttentions}${APIS.ATTENTIONS}`,
                JSON.stringify(body),
                {
                    headers: queryHeaders,
                    observe: 'response',
                },
            )
            .pipe<any>(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
            );
    }

    getTenant(tenantId: string): Observable<Tenant> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .get<HttpBaseResponse<any>>(`${environment.apiAuthService}${APIS.TENANTS}/${tenantId}`, {
                headers: queryHeaders,
                observe: 'response',
            })
            .pipe(
                map<HttpResponse<any>, Tenant>((response) => {
                    return response.body.data;
                }),
            );
    }

    // TODO: MOCK -> Borrar y dejar el patchTenant2
    patchTenant(tenant: Partial<Tenant>): Observable<any> {
        return of(tenant).pipe(
            delay(3000),
            tap(tenant => this.updateCurrentTenant(tenant))
        );
    }

    patchTenant2(tenant: Partial<Tenant>) {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        return this.http
            .post<any>(
                `${environment.apiAttentions}${APIS.ATTENTIONS}`,
                JSON.stringify(tenant),
                {
                    headers: queryHeaders,
                    observe: 'response',
                },
            )
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body.data;
                }),
                tap((tenant) => { this.updateCurrentTenant(tenant); }),
            );
    }

    // Validate if user is admin from in-memory user profile
    isAdmin(): boolean {
        const userRoles = this.userValue?.['roles'] as Array<string> | undefined;

        if (userRoles) {
            for (let role in userRoles) {

                if (userRoles[role].toUpperCase().includes(USERADMIN.ROLE)) {
                    return true;
                }
            }
        }
        return false;
    }

    // Validate if user is frontdesk from in-memory user profile
    isFrontDesk(): boolean {
        const userRoles = this.userValue?.['roles'] as Array<string> | undefined;

        for (let role in userRoles) {

            if (userRoles[role].toUpperCase().includes(USERFRONTDESK.ROLE)) {
                return true;
            }
        }
        return false;
    }

    getValueTenant(): Tenant {
        return this.tenant$.getValue();
    }

    loginV3(username: string, password?: string, tenantId?: number, targetTenantUUID?: String): Observable<HttpBaseResponse<PortalLoginData>> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const body = {
            username: username,
            password: password,
            applicationId: this.applicationId,
            tenantId,
            targetTenantUUID
        };

        return this.http
            .post<any>(`${environment.apiAuthService}${APIS.LOGIN_V3}`, body, {
                headers: queryHeaders,
                observe: 'response',
                withCredentials: true,
            })
            .pipe(
                map<HttpResponse<any>, any>((response) => {
                    return response.body;
                }),
            );
    }

    getTenants(): Observable<UserTenantAccess[]> {
        const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
        const queryParams: HttpParams = new HttpParams().append('applicationId', this.applicationId.toString());
        return this.http
            .get<HttpBaseResponse<any>>(`${environment.apiAuthService}/users${APIS.TENANTS}`, {
                headers: queryHeaders,
                observe: 'response',
                params: queryParams
            })
            .pipe(
                map<HttpResponse<any>, UserTenantAccess[]>((response) => {
                    return response.body;
                }),
            );
    }
}

export function bootstrapOperadoresAuthSession(auth: AuthenticationService): () => Promise<void> {
    return () => firstValueFrom(auth.bootstrapSessionFromCookies());
}
